import React, { useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Text,
  SearchBox,
  CommandBar,
  ICommandBarItemProps,
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  IColumn,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Spinner,
  MessageBar,
  MessageBarType,
  Modal,
  TextField,
} from '@fluentui/react';
import { IApprovalFlow, IUserPermissions } from '../../shared/models';
import { approvalFlowApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';
import axios from 'axios';

// Erweiterte Flow-Interface mit Lookup-Feldern
interface IApprovalFlowEnriched extends IApprovalFlow {
  BestellungTitle: string;
  PositionArtikelname: string;
  LastStep: string;
}

export const FreigabenListe: React.FC = () => {
  const { user } = useAuth();
  const [flows, setFlows] = useState<IApprovalFlowEnriched[]>([]);
  const [filteredFlows, setFilteredFlows] = useState<IApprovalFlowEnriched[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<IApprovalFlowEnriched[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);

  // Dialog States
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<IApprovalFlowEnriched | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approveDialogVisible, setApproveDialogVisible] = useState(false);
  const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    loadFlows();
    loadPermissions();
  }, []);

  useEffect(() => {
    filterFlows();
  }, [searchText, flows]);

  const loadPermissions = async () => {
    try {
      if (user?.email) {
        const response = await axios.get('/api/permissions/me', {
          params: { email: user.email }
        });
        setPermissions(response.data);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Berechtigungen:', err);
    }
  };

  const loadFlows = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approvalFlowApi.getAllFlows();
      setFlows(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Freigaben');
    } finally {
      setLoading(false);
    }
  };

  const filterFlows = () => {
    let filtered = [...flows];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((f) =>
        f.BestellungTitle?.toLowerCase().includes(searchLower) ||
        f.PositionArtikelname?.toLowerCase().includes(searchLower) ||
        f.LastStep?.toLowerCase().includes(searchLower) ||
        f.OverallStatus?.toLowerCase().includes(searchLower)
      );
    }

    // Sortierung: Flows mit offenen Steps für den aktuellen Benutzer oben
    const userEmail = user?.email?.toLowerCase();
    filtered.sort((a, b) => {
      const aHasUserStep = a.Steps?.some(
        (step) => step.ApproverEmail?.toLowerCase() === userEmail && step.Status === 'Offen'
      );
      const bHasUserStep = b.Steps?.some(
        (step) => step.ApproverEmail?.toLowerCase() === userEmail && step.Status === 'Offen'
      );

      if (aHasUserStep && !bHasUserStep) return -1;
      if (!aHasUserStep && bHasUserStep) return 1;
      return 0;
    });

    setFilteredFlows(filtered);
  };

  const isAdminOrEinkauf = useMemo(() => {
    if (!permissions) return false;
    return permissions.isAdmin || permissions.isEinkauf;
  }, [permissions]);

  const handleDeleteSingle = async (flow: IApprovalFlowEnriched) => {
    if (!flow.Id) return;

    try {
      setSaving(true);
      setError(null);
      await approvalFlowApi.deleteFlow(flow.Id);
      await loadFlows();
      setDeleteDialogOpen(false);
      setSelectedFlow(null);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Freigabe');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const flow of selectedItems) {
        if (flow.Id) {
          await approvalFlowApi.deleteFlow(flow.Id);
        }
      }

      await loadFlows();
      setBulkDeleteDialogOpen(false);
      setSelectedItems([]);
      setSelectionMode(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Freigaben');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveSelected = async () => {
    if (selectedItems.length === 0 || !user?.email) return;

    setApproving(true);
    try {
      for (const flow of selectedItems) {
        if (flow.Id) {
          // Genehmige den Flow für den aktuellen Benutzer
          await approvalFlowApi.approveStep(flow.Id, user.email);
        }
      }
      setApproveDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Freigeben');
    } finally {
      setApproving(false);
    }
  };

  const handleRejectSelected = async () => {
    if (selectedItems.length === 0 || !user?.email) return;

    setRejecting(true);
    try {
      for (const flow of selectedItems) {
        if (flow.Id) {
          // Lehne den Flow für den aktuellen Benutzer ab
          await approvalFlowApi.rejectStep(flow.Id, user.email);
        }
      }
      setRejectDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Ablehnen');
    } finally {
      setRejecting(false);
    }
  };

  const openViewDialog = (flow: IApprovalFlowEnriched) => {
    setSelectedFlow(flow);
    setViewDialogOpen(true);
  };

  const openDeleteDialog = (flow: IApprovalFlowEnriched) => {
    setSelectedFlow(flow);
    setDeleteDialogOpen(true);
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Freigegeben':
        return '#107c10';
      case 'Abgelehnt':
        return '#d13438';
      case 'Teilweise freigegeben':
        return '#ca5010';
      case 'Offen':
      default:
        return '#605e5c';
    }
  };

  const columns: IColumn[] = [
    {
      key: 'BestellungTitle',
      name: 'Bestellung',
      fieldName: 'BestellungTitle',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'PositionArtikelname',
      name: 'Artikel',
      fieldName: 'PositionArtikelname',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'LastStep',
      name: 'Letzter Schritt',
      fieldName: 'LastStep',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'OverallStatus',
      name: 'Status',
      fieldName: 'OverallStatus',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: IApprovalFlowEnriched) => (
        <Text style={{ color: getStatusColor(item.OverallStatus), fontWeight: 600 }}>
          {item.OverallStatus}
        </Text>
      ),
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: IApprovalFlowEnriched) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <IconButton
            iconProps={{ iconName: 'View' }}
            title="Ansehen"
            onClick={() => openViewDialog(item)}
          />
          {isAdminOrEinkauf && (
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Löschen"
              onClick={() => openDeleteDialog(item)}
            />
          )}
        </Stack>
      ),
    },
  ];

  const commandBarItems: ICommandBarItemProps[] = useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    // "Auswählen" Button
    items.push({
      key: 'select',
      text: selectionMode ? 'Auswahl beenden' : 'Auswählen',
      iconProps: { iconName: 'CheckboxComposite' },
      onClick: () => {
        setSelectionMode(!selectionMode);
        setSelectedItems([]);
      },
      disabled: !isAdminOrEinkauf,
    });

    // "Freigeben" Button (nur wenn Elemente ausgewählt und canApprove)
    if (selectionMode && selectedItems.length > 0 && permissions?.canApprove) {
      items.push({
        key: 'approve',
        text: `Freigeben (${selectedItems.length})`,
        iconProps: { iconName: 'CheckMark' },
        onClick: () => setApproveDialogVisible(true),
      });
    }

    // "Ablehnen" Button (nur wenn Elemente ausgewählt und canApprove)
    if (selectionMode && selectedItems.length > 0 && permissions?.canApprove) {
      items.push({
        key: 'reject',
        text: `Ablehnen (${selectedItems.length})`,
        iconProps: { iconName: 'Cancel' },
        onClick: () => setRejectDialogVisible(true),
      });
    }

    // "Löschen" Button (nur wenn Elemente ausgewählt und Admin/Einkauf)
    if (selectionMode && selectedItems.length > 0 && isAdminOrEinkauf) {
      items.push({
        key: 'deleteSelected',
        text: `Löschen (${selectedItems.length})`,
        iconProps: { iconName: 'Delete' },
        onClick: () => setBulkDeleteDialogOpen(true),
      });
    }

    return items;
  }, [selectionMode, selectedItems, permissions, isAdminOrEinkauf]);

  const selection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          setSelectedItems(selection.getSelection() as IApprovalFlowEnriched[]);
        },
      }),
    []
  );

  if (loading) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { height: '100vh' } }}>
        <Spinner label="Lade Freigaben..." />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: 20 } }}>
      <Text variant="xxLarge">Freigaben</Text>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <CommandBar items={commandBarItems} />

      <Stack horizontal tokens={{ childrenGap: 10 }}>
        <SearchBox
          placeholder="Freigaben suchen (Bestellung, Artikel, Schritt, Status)..."
          value={searchText}
          onChange={(_, newValue) => setSearchText(newValue || '')}
          styles={{ root: { width: 400 } }}
        />
      </Stack>

      <DetailsList
        items={filteredFlows}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        selection={selection}
        selectionMode={selectionMode ? SelectionMode.multiple : SelectionMode.none}
        selectionPreservedOnEmptyClick
      />

      {/* View Dialog */}
      <Modal
        isOpen={viewDialogOpen}
        onDismiss={() => setViewDialogOpen(false)}
        styles={centeredModalStyles}
      >
        <div style={modalContentStyles.header}>
          <Text variant="xLarge">Freigabe ansehen</Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setViewDialogOpen(false)}
          />
        </div>
        <div style={modalContentStyles.body}>
          {selectedFlow && (
            <Stack tokens={{ childrenGap: 10 }}>
              <TextField label="Bestellung" value={selectedFlow.BestellungTitle} readOnly />
              <TextField label="Artikel" value={selectedFlow.PositionArtikelname} readOnly />
              <TextField label="Abteilung" value={selectedFlow.DepartmentName} readOnly />
              <TextField label="Flow-Typ" value={selectedFlow.FlowType} readOnly />
              <TextField label="Aktueller Schritt" value={selectedFlow.CurrentStep?.toString()} readOnly />
              <TextField label="Letzter Schritt" value={selectedFlow.LastStep} readOnly />
              <TextField label="Status" value={selectedFlow.OverallStatus} readOnly />
              <Stack>
                <Text variant="medium" styles={{ root: { fontWeight: 600, marginBottom: 8 } }}>
                  Genehmigungsschritte:
                </Text>
                {selectedFlow.Steps && selectedFlow.Steps.map((step, index) => (
                  <Stack
                    key={index}
                    tokens={{ childrenGap: 4 }}
                    styles={{
                      root: {
                        padding: 8,
                        marginBottom: 8,
                        border: '1px solid #edebe9',
                        borderRadius: 2,
                      }
                    }}
                  >
                    <Text variant="small">
                      <strong>Schritt {step.StepNumber}:</strong> {step.StepType} - {step.ApproverName}
                    </Text>
                    <Text variant="small">
                      Status: <span style={{ color: getStatusColor(step.Status), fontWeight: 600 }}>
                        {step.Status}
                      </span>
                    </Text>
                    {step.ApprovedDate && (
                      <Text variant="small">
                        Datum: {new Date(step.ApprovedDate).toLocaleString('de-DE')}
                      </Text>
                    )}
                    {step.Comments && (
                      <Text variant="small">Kommentar: {step.Comments}</Text>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </div>
        <div style={modalContentStyles.footer}>
          <PrimaryButton text="Schließen" onClick={() => setViewDialogOpen(false)} />
        </div>
      </Modal>

      {/* Delete Dialog */}
      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={() => setDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Freigabe löschen',
          subText: `Möchten Sie die Freigabe für "${selectedFlow?.BestellungTitle}" (${selectedFlow?.PositionArtikelname}) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={() => selectedFlow && handleDeleteSingle(selectedFlow)} disabled={saving} />
        </DialogFooter>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        hidden={!bulkDeleteDialogOpen}
        onDismiss={() => setBulkDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Freigaben löschen',
          subText: `Möchten Sie ${selectedItems.length} Freigaben wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setBulkDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={handleBulkDelete} disabled={saving} />
        </DialogFooter>
      </Dialog>

      {/* Freigeben Dialog */}
      <Dialog
        hidden={!approveDialogVisible}
        onDismiss={() => setApproveDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Freigaben genehmigen',
          subText: `Möchten Sie ${selectedItems.length} Freigabe${selectedItems.length !== 1 ? 'n' : ''} wirklich genehmigen? Die ausgewählten Freigabe-Schritte werden für Sie freigegeben.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleApproveSelected} text="Freigeben" disabled={approving} />
          <DefaultButton onClick={() => setApproveDialogVisible(false)} text="Abbrechen" disabled={approving} />
        </DialogFooter>
      </Dialog>

      {/* Ablehnen Dialog */}
      <Dialog
        hidden={!rejectDialogVisible}
        onDismiss={() => setRejectDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Freigaben ablehnen',
          subText: `Möchten Sie ${selectedItems.length} Freigabe${selectedItems.length !== 1 ? 'n' : ''} wirklich ablehnen? Die ausgewählten Freigabe-Schritte werden für Sie abgelehnt.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleRejectSelected} text="Ablehnen" disabled={rejecting} />
          <DefaultButton onClick={() => setRejectDialogVisible(false)} text="Abbrechen" disabled={rejecting} />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
